import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { auth } from '../lib/auth';

interface AuthSplitScreenProps {
  onComplete?: () => void;
}

const carouselImages = [
  "/brand-assets/signup/image-1.jpeg",
  "/brand-assets/signup/image-2.jpeg",
  "/brand-assets/signup/image-3.jpeg"
];

export function AuthSplitScreen({ onComplete }: AuthSplitScreenProps) {
  const [isSignUpMode, setIsSignUpMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Rotate images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const isFormValid = () => {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const passwordValid = password.length >= 8;
    
    if (isSignUpMode) {
      return emailValid && passwordValid && firstName.trim() && lastName.trim();
    }
    return emailValid && passwordValid;
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;
    
    setError(null);
    setIsLoading(true);

    try {
      if (isSignUpMode) {
        await auth.signUp(email, password, {
          first_name: firstName,
          last_name: lastName,
        });
      } else {
        await auth.signIn(email, password);
      }
      onComplete?.();
    } catch (err: any) {
      console.error('Auth error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await auth.signInWithProvider('google');
    } catch (err: any) {
      console.error('Google auth error:', err);
      setError(err.message || 'An error occurred during Google sign-in.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await auth.resetPassword(email);
      setResetEmailSent(true);
      setError(null);
      // Show success message
      setTimeout(() => {
        setResetEmailSent(false);
      }, 10000); // Hide after 10 seconds
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to send reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await auth.signInWithProvider('apple');
    } catch (err: any) {
      console.error('Apple auth error:', err);
      setError(err.message || 'An error occurred during Apple sign-in.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Column - Form (40% width on desktop) */}
      <div className="lg:w-[40%] flex flex-col justify-center p-8 lg:pl-20 lg:pr-12" style={{ backgroundColor: '#F2F0E5' }}>
        <div className="w-full max-w-md mx-0">
          {/* Logo */}
          <div className="mb-8 -ml-10">
            <img 
              src="https://static.wixstatic.com/media/654322_f4aebad34f274603a614b55aed8193af~mv2.png/v1/fill/w_1360,h_232,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/654322_f4aebad34f274603a614b55aed8193af~mv2.png"
              alt="Purpose Collective"
              className="h-[75px] w-auto"
            />
          </div>

          {/* Form */}
          <div>
            <h1 className="text-3xl text-gray-900 mb-2 block" style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 400, letterSpacing: '-0.02em' }}>
              {isSignUpMode ? 'Create an account' : 'Welcome back'}
            </h1>
            <p className="text-gray-600 mb-8" style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 300, letterSpacing: '-0.01em' }}>
              {isSignUpMode 
                ? "Become part of a global community shaping conscious culture." 
                : "Sign in to continue to your account."}
            </p>

            {/* Form */}
            <form onSubmit={handleCreateAccount} className="space-y-8">
              {/* Error and Success Messages */}
              {error && !resetEmailSent && (
                <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
                  {error}
                </div>
              )}

              {resetEmailSent && (
                <div className="p-3 text-sm text-[#57241F] bg-[#F5E5E3] rounded-lg">
                  Password reset email sent! Check your inbox.
                </div>
              )}
              {/* Name Fields - Only show in sign up mode */}
              {isSignUpMode && (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full py-2 bg-transparent border-0 border-b border-gray-400 focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-500"
                      style={{ fontFamily: 'Canela, Georgia, serif', fontSize: '1rem', letterSpacing: '-0.01em' }}
                      placeholder="First name"
                      required={isSignUpMode}
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full py-2 bg-transparent border-0 border-b border-gray-400 focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-500"
                      style={{ fontFamily: 'Canela, Georgia, serif', fontSize: '1rem', letterSpacing: '-0.01em' }}
                      placeholder="Last name"
                      required={isSignUpMode}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full py-2 bg-transparent border-0 border-b border-gray-400 focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-500"
                  style={{ fontFamily: 'Canela, Georgia, serif', fontSize: '1rem', letterSpacing: '-0.01em' }}
                  placeholder="Email address"
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Password Field */}
              <div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full py-2 pr-10 bg-transparent border-0 border-b border-gray-400 focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-500"
                    style={{ fontFamily: 'Canela, Georgia, serif', fontSize: '1rem', letterSpacing: '-0.01em' }}
                    placeholder={isSignUpMode ? "Password (minimum 8 characters)" : "Password"}
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {isSignUpMode && password && password.length < 8 && (
                  <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters</p>
                )}
              </div>

              {/* Forgot Password Link - Only show in login mode */}
              {!isSignUpMode && (
                <div className="flex justify-end -mt-2 mb-4">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !isFormValid()}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-all bg-black text-white ${
                  isFormValid() && !isLoading
                    ? 'opacity-100 hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2'
                    : 'opacity-40 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isSignUpMode ? 'Creating account...' : 'Signing in...'}
                  </span>
                ) : (
                  isSignUpMode ? 'Create account' : 'Sign in'
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 text-gray-500" style={{ backgroundColor: '#F2F0E5' }}>Or continue with</span>
              </div>
            </div>

            {/* Social Sign In */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full py-2.5 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-3 font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>

              <button
                type="button"
                onClick={handleAppleSignIn}
                disabled={isLoading}
                className="w-full py-2.5 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-3 font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Apple
              </button>
            </div>

            {/* Footer */}
            <p className="text-center mt-8 text-sm text-gray-600">
              {isSignUpMode ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                type="button"
                onClick={() => {
                  setIsSignUpMode(!isSignUpMode);
                  setError(null);
                }}
                className="font-medium text-gray-900 hover:underline focus:outline-none focus:underline"
              >
                {isSignUpMode ? 'Log in' : 'Sign up'}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right Column - Images (60% width on desktop) */}
      <div className="lg:w-[60%] relative bg-gray-900 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <img
              src={carouselImages[currentImageIndex]}
              alt="Purpose Collective World"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>

        {/* Progress indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`h-1 rounded-full transition-all ${
                index === currentImageIndex 
                  ? 'w-8 bg-white' 
                  : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}